/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * CheckoutReturnPage — where the gateway drops the runner after they pay.
 *
 * THIS PAGE DECIDES NOTHING. Anyone can type this URL, and whatever the
 * gateway appends to it is browser-visible and unsigned. Only a
 * signature-verified webhook marks a payment paid; this page asks the server
 * what that webhook already decided, and shows it.
 *
 * WHY IT POLLS. The redirect and the webhook are independent and neither waits
 * for the other — the browser usually wins by a second or two. So the runner
 * lands here before their registration exists, and the only way to know when
 * it does is to ask again. Briefly: a handful of tries on a widening gap, then
 * an honest "we will confirm by email" rather than a spinner with no end.
 *
 * IT WAITS ON `fulfilled`, NOT ON `status`. 'paid' means the money moved,
 * which happens a moment before the rows the runner came back for exist.
 * Rendering on 'paid' alone would tell somebody their entry is confirmed while
 * their passes are still being written.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import envConfig from '../config/envConfig';
import { fetchPaymentStatus } from '../components/athlohive/event-detail/checkoutClient';
import { rememberEntered } from '../components/athlohive/event-detail/registrationMemory';
import { checkInOpensHint } from '../components/athlohive/event-detail/selfCheckInRules';
import { rememberPending, forgetPending } from '../components/athlohive/event-detail/pendingPayment';
import { isUserLoggedIn } from '../utils/utils';
import { pageHref } from '../utils/pageHref';
// This page is reached by a redirect from the gateway, not by navigation from
// the event page, so nothing has loaded a stylesheet for it. Without this
// import the whole screen renders as unstyled HTML.
import '../styles/checkoutReturn.css';

// The Page Builder pages this lands people on. Routed through pageHref, and
// named by the same slugs eventButtons.js uses, rather than hand-written paths:
// '/my-entries' and '/events' were invented here and 404, because neither is a
// route this app has.
const MY_REGISTRATIONS_SLUG = 'my-registrations';
const EVENT_LIST_PATH = '/p/event';

/**
 * The event this payment was for.
 *
 * Read from the line items rather than the URL, for the same reason the
 * CONFIRMED branch below reads it there: the gateway's redirect carries only
 * the order id, and the event rides on the chargeable entity's parent. Null
 * when the status reply has no line items yet, which is every phase before the
 * webhook lands -- so callers must be able to leave the link out.
 */
export function eventIdOf(payment) {
  return payment?.line_items?.[0]?.chargeable_entity_parent_id || null;
}

/** `/p/event/:recordId` — the route App.js serves the event page on. */
export const eventHref = (eventId) => `${EVENT_LIST_PATH}/${eventId}`;

/** How long to keep asking, and how fast. Widening, so a slow webhook is not
 *  hammered and a fast one is still caught almost immediately. */
const POLL_DELAYS_MS = [700, 1000, 1500, 2000, 2500, 3000, 4000];

const WAITING = 'waiting';
const CONFIRMED = 'confirmed';
const FAILED = 'failed';
const SLOW = 'slow';
const UNKNOWN = 'unknown';

export function orderIdFromSearch(search) {
  try {
    return new URLSearchParams(search || '').get('order_id') || null;
  } catch (err) {
    return null;
  }
}

/** What one status reply means for what the runner should see. */
export function phaseFor(status) {
  if (!status) return null;
  if (status.status === 'paid') {
    // A PAID PAYMENT EITHER HAS ITS ROWS OR IT IS NOT FINISHED YET. There is
    // no third outcome to render: the places are reserved before the gateway
    // is called, so a cart that does not fit is refused with
    // `insufficient_capacity` and nothing is charged. Nobody completes a
    // payment and ends up without the entry they paid for.
    return status.fulfilled ? CONFIRMED : WAITING;
  }
  if (status.status === 'cancelled' || status.status === 'other') return FAILED;
  // Still 'in_progress' on our side, but the status endpoint asks the gateway
  // directly for these and passes the answer through as `gateway_status`. A
  // gateway that says the payment failed is better evidence than our own row,
  // which is only waiting for a webhook that will say the same thing. Stopping
  // here means a dead payment is told so in a second rather than after the
  // full poll budget expires into a screen that implies it succeeded.
  if (status.gateway_status === 'payment_failed') return FAILED;
  return WAITING;
}

/* Drawn rather than imported: two paths, no icon dependency, and they inherit
   the mark's colour so the phase tints the glyph for free. */
const TickIcon = (
  <svg viewBox="0 0 32 32"><path className="chk__draw" d="M7 16.8 13.2 23 25 10.4" /></svg>
);
const CrossIcon = (
  <svg viewBox="0 0 32 32">
    <path className="chk__draw" d="M10 10 22 22" />
    <path className="chk__draw" d="M22 10 10 22" />
  </svg>
);
const ClockIcon = (
  <svg viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="11" className="chk__draw chk__draw--ring" />
    <path className="chk__draw" d="M16 9.5V16l4.5 3" />
  </svg>
);
const QueryIcon = (
  <svg viewBox="0 0 32 32">
    <path className="chk__draw" d="M12 12a4 4 0 1 1 5 3.9V19" />
    <path className="chk__draw" d="M17 23.2h.01" />
  </svg>
);

const plural = (payment) => (payment?.line_items?.length === 1 ? 'place is' : 'places are');

/* One entry per phase. Keeping the copy here rather than inline in the JSX is
   what lets the markup above stay a single shape for all five. */
const VIEWS = {
  [UNKNOWN]: {
    mark: 'idle',
    icon: QueryIcon,
    title: 'We could not tell which payment this is',
    body: () => 'If you have just paid, your entry is safe \u2014 open your entries to check. Nothing is charged twice.',
    actions: (
      <div className="chk__acts">
        <a className="chk__btn chk__btn--go" href={pageHref(MY_REGISTRATIONS_SLUG)}>Open your entries</a>
        <a className="chk__btn chk__btn--quiet" href={EVENT_LIST_PATH}>Back to events</a>
      </div>
    ),
  },
  [WAITING]: {
    mark: 'spin',
    icon: null,
    title: 'Confirming your payment\u2026',
    body: () => 'This may take a few moments. We are waiting for your bank to confirm.',
    actions: null,
  },
  [CONFIRMED]: {
    mark: 'ok',
    icon: TickIcon,
    title: 'You are in.',
    // The return carries only the order, not the event's start, so the
    // check-in line states the rule rather than a clock time.
    body: (payment) => `Payment received and your ${plural(payment)} confirmed. ${checkInOpensHint(null)}`,
    /*
      The passes are NOT read from here. The status endpoint reports which rows
      were created and nothing about their contents -- a passcode and its QR are
      entry credentials, and they come from the owner-scoped surface built to
      hand them out safely.
    */
    actions: (payment) => {
      // Omitted rather than pointed at the list when the reply carried no line
      // items: a button labelled for one event that opens every event is worse
      // than no button, and CONFIRMED almost always has them.
      const eventId = eventIdOf(payment);
      return (
        <div className="chk__acts">
          <a className="chk__btn chk__btn--go" href={pageHref(MY_REGISTRATIONS_SLUG)}>View your passes</a>
          {eventId && (
            <a className="chk__btn chk__btn--quiet" href={eventHref(eventId)}>Go to event page</a>
          )}
          <a className="chk__btn chk__btn--quiet" href={EVENT_LIST_PATH}>Browse more events</a>
        </div>
      );
    },
  },
  [FAILED]: {
    mark: 'bad',
    icon: CrossIcon,
    title: 'That payment did not go through',
    body: () => 'Nothing was charged. Please retry the payment to complete registration.',
    actions: (
      <div className="chk__acts">
        <a className="chk__btn chk__btn--go" href={EVENT_LIST_PATH}>Back to events</a>
      </div>
    ),
  },
  /* TWO SITUATIONS LAND HERE, AND THIS SCREEN DELIBERATELY SAYS ONE THING.

     The budget runs out either because the money moved and the rows are late
     (status 'paid'), or because the payment never resolved at all (still
     'in_progress' — a UPI collect sitting unapproved on someone's phone).

     WHY THE COPY NO LONGER SPLITS ON THAT. By the time this is read, polling
     has stopped. The status in hand is the LAST reply, not the current one,
     and nothing remains that could refresh it. Branching on a frozen value
     presents a stale reading as a present fact: "Payment received" would sit
     on screen for a payment that has since failed, with no way to take it
     back. Neither branch offered a retry either, so the split gave the runner
     nothing to act on that the other did not.

     WHAT IT SAYS INSTEAD is true whichever way the payment went, and carries
     the one instruction that matters on this screen: do not pay again. This is
     exactly where a worried runner pays twice. The email is promised
     conditionally — "as soon as it is confirmed" — so it commits to nothing
     for a payment that never lands. It does not claim the money arrived,
     because that is the error with a real cost: it stops somebody watching an
     unapproved request, which then expires, and they hold no place. */
  [SLOW]: {
    mark: 'wait',
    icon: ClockIcon,
    title: 'This is taking longer than usual',
    body: () => 'Your payment is still being confirmed. '
      + 'We will email you as soon as it is confirmed.',
    actions: (
      <div className="chk__acts">
        <a className="chk__btn chk__btn--go" href={pageHref(MY_REGISTRATIONS_SLUG)}>Check your entries</a>
      </div>
    ),
  },
};

export default function CheckoutReturnPage() {
  const orderId = orderIdFromSearch(window.location.search);
  const [phase, setPhase] = useState(orderId ? WAITING : UNKNOWN);
  const [payment, setPayment] = useState(null);
  const timerRef = useRef(null);
  // Which order a poll chain has already been started for — see the effect below.
  const startedForRef = useRef(null);
  // Whether ANY poll was ever answered. Separates "we asked repeatedly and it
  // is still unresolved" from "we never got an answer at all" — the second
  // happens when the session does not survive the gateway redirect, since
  // /arcee/payments/status/ 404s a payment it cannot match to the signed-in
  // buyer. Reporting that as a slow payment would be inventing a fact we do
  // not have; UNKNOWN already says the true thing.
  const answeredRef = useRef(false);

  const poll = useCallback(async (attempt) => {
    const status = await fetchPaymentStatus({
      applicationId: envConfig.applicationId,
      gatewayOrderId: orderId,
    }).catch(() => null);

    if (status) {
      answeredRef.current = true;
      setPayment(status);
      const next = phaseFor(status);

      /* THE NOTE THE EVENT PAGE READS.
         A paid entry writes no rows until the webhook fires, so until then the
         event page has nothing to go on and offers Register again -- a second
         payment for the same place. This is the one moment both halves of the
         note exist together: the order id came in on the gateway's redirect,
         and the event id rides on the line items.
         Written while unresolved, and removed the instant it settles either
         way. Removal matters more than writing: a note that outlives its
         payment hides the button from somebody who abandoned the checkout and
         holds no place at all. */
      const noteEventId = eventIdOf(status);
      if (noteEventId) {
        if (next === CONFIRMED || next === FAILED) {
          forgetPending(envConfig.organizationId, envConfig.applicationId, noteEventId);
        } else {
          const noteWho = await isUserLoggedIn().catch(() => null);
          rememberPending(envConfig.organizationId, envConfig.applicationId, noteEventId, {
            orderId,
            userId: noteWho?.user_id,
          });
        }
      }

      if (next === CONFIRMED) {
        // F7. Remembered HERE, and only here, because this is the first moment
        // the entry is real: the money moved AND the rows exist. Writing it
        // when checkout started would withdraw the Register button from a
        // runner who then abandoned the payment and holds no place at all.
        //
        // The event id travels on the line items as the chargeable entity's
        // parent, so the page never needed it in the URL.
        const eventId = eventIdOf(status);
        if (eventId) {
          // Stamped with who it belongs to: the event page drops a marker that
          // is not the person signed in, so one without an id would be thrown
          // away on the next visit and the button offered again.
          const who = await isUserLoggedIn().catch(() => null);
          rememberEntered(
            envConfig.organizationId, envConfig.applicationId,
            eventId, status.line_items.length, who?.user_id,
          );
        }
      }
      if (next === CONFIRMED || next === FAILED) {
        setPhase(next);
        return;
      }
    }

    // Out of tries. NOT an error: the payment may well be fine and the webhook
    // simply late, so this says what is actually true rather than implying
    // something failed. Which "true thing" that is depends on whether we ever
    // heard back at all — SLOW speaks about a payment we can see, UNKNOWN about
    // one we never could.
    if (attempt >= POLL_DELAYS_MS.length) {
      setPhase(answeredRef.current ? SLOW : UNKNOWN);
      return;
    }
    timerRef.current = setTimeout(() => poll(attempt + 1), POLL_DELAYS_MS[attempt]);
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return undefined;

    // ONE CHAIN PER ORDER. StrictMode mounts this effect, tears it down and
    // mounts it again, which started two independent poll chains against
    // /arcee/payments/status/. timerRef only ever held the second, so the first
    // was never cleared on unmount and kept polling on a page nobody was on.
    //
    // The guard is keyed on the order rather than a bare boolean so a genuine
    // change of orderId still starts a fresh chain.
    if (startedForRef.current === orderId) return undefined;
    startedForRef.current = orderId;

    poll(0);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [orderId, poll]);

  // The mark and the words are the same four slots in every phase, so a phase
  // change swaps contents rather than layout and nothing moves under a reader.
  const view = VIEWS[phase];

  return (
    <div className="chk">
      <main className="chk__card" role="status" aria-live="polite">
        <div
          className={`chk__mark chk__mark--${view.mark}`}
          aria-hidden="true"
        >
          {view.icon}
        </div>

        <h1 className="chk__h">
          {typeof view.title === 'function' ? view.title(payment) : view.title}
        </h1>
        <p className="chk__p">{view.body(payment)}</p>

        {phase === WAITING && (
          <>
            <p className="chk__hold">Please keep this page open.</p>
            <div className="chk__dots" aria-hidden="true"><i /><i /><i /></div>
          </>
        )}

        {typeof view.actions === 'function' ? view.actions(payment) : view.actions}

        {orderId && phase !== CONFIRMED && (
          <p className="chk__ref">
            Reference <code>{orderId}</code>
          </p>
        )}
      </main>
    </div>
  );
}
